import {
  Box,
  Button,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { KeyboardDatePicker, KeyboardTimePicker } from "@material-ui/pickers";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import apiHelper from "../../../../util/ApiHelper";
import { useLoading } from "../../../../util/LoadingProvider";
import { modal } from "../../../../util";
import UploadImage from "../../UploadImage";
import dayjs from "dayjs";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";

const useStyles = makeStyles((theme) => ({
  innerBox: {
    background: "#ffffff",
    marginBottom: theme.spacing(4),
    "& *": {
      fontSize: "13px",
      lineHeight: "15px",
    },
  },
  sectionBox: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: "300",
    fontSize: "13px",
    lineHeight: "15px",
    color: "#333333",
  },
  itemTitle: {
    fontWeight: "bold",
    fontSize: "12px",
    lineHeight: "14px",
    color: "#333333",
  },
  itemValue: {
    fontWeight: "normal",
    fontSize: "14px",
    lineHeight: "16px",
    color: "#333333",
  },
  input: {
    width: "100%",
    "& button": {
      fontSize: "13px",
      lineHeight: "15px",
      width: "25%",
    },
  },
  buttonGroup: {
    textAlign: "right",
    marginTop: "-20px",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  customLabel: {
    margin: "0 !important",
    "& label": {
      color: "black",
      fontWeight: 700,
      fontSize: "16px",
    },
    "& p": {
      fontSize: "12px",
    },
  },
}));

const defaultFormValues = {
  "id": null,
  "logoGroupId": null,
  "titleEn": "",
  "titleTc": "",
  "logoUrl": "",
  "seq": null,
  "createTime": dayjs().format("YYYY-MM-DDTHH:mm:ss[Z]"),
  "updateTime": dayjs().format("YYYY-MM-DDTHH:mm:ss[Z]"),
}

function LogoEditPage({ disabled, ...props }) {
  const classes = useStyles();
  const { match, history } = props;
  const { id } = match.params;
  const [paramId, setParamId] = React.useState(id);
  const [defaultValue, setDefaultValue] = React.useState(defaultFormValues);
  const { setLoading } = useLoading();
  const [groupList, setGroupList] = React.useState("");
  const [group, setGroup] = React.useState("");

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValue, [defaultValue]),
  });

  const onSubmit = async (value) => {
    setLoading(true);
    // purge date and time
    const { date, time, ...data } = value;

    const displayDatetime = `${date} ${dayjs(time).format("HH:mm:ss")}`;
    data.displayDatetime = displayDatetime;
    data.logoGroupId = group;
    try {
      if (paramId !== "new") {
        const result = await apiHelper.hkiff.updateLogoById(paramId, data);
        if (result.status === 200) {
          toast.dark("Logo is updated.");
        }
      } else {
        const result = await apiHelper.hkiff.postLogo(data);
        if (result.status === 200) {
          setParamId(result.data.id);
          history.push(`/hkiff/logos/listing/${result.data.id}`);
          toast.dark("Logo is created.");
        }
      }
    } catch (e) {
      console.log(e);
    }
    setLoading(false);
  };

  const getLogoById = React.useCallback(async () => {
    try {
      const result = await apiHelper.hkiff.getLogoById(id);
      setDefaultValue(result.data);
      reset(result.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [id, reset, setLoading]);

  const handleSelectChange = (e) => {
    setGroup(e.target.value);
  };

  const handleDelete = async () => {
    const decision = await modal.fire({
      title: "Delete Logo",
      text: "Are you sure to delete this logo?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        setLoading(true);
        await apiHelper.hkiff.deleteLogoById(paramId);
        toast.dark("The logo is deleted.");
        history.push("/hkiff/logos/listing");
      } catch (err) {
        console.log(err);
        toast.error("Failed to delete logo.");
      } finally {
        setLoading(false);
      }
    }
  };

  React.useEffect(() => {
    setLoading(true);
    apiHelper.hkiff
      .listGroupParent()
      .then((response) => {
        var list = response.data;
        setGroupList(list);
        console.log('group list:', list);
      }).catch(function (error) {
        console.log(error);
      });
    if (id !== "new") getLogoById();
    setLoading(false);
  }, [getLogoById, id]);

  React.useEffect(() => {
    if(groupList!==""){
      setGroup(defaultValue.logoGroupId);
    }
  }, [groupList]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {`Using Our Logo > ${id === "new" ? "Add" : "Edit"} Logo`}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={disabled} className="fieldset">
          <Box className={classes.innerBox}>
            <Box className={classes.sectionBox}>
              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <strong>Logo Details</strong>
                </Grid>
              </Grid>
              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="titleEn"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Title (English)"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        fullWidth
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="titleTc"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Title (Traditional Chinese)"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        fullWidth
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth margin="dense" variant="outlined">
                    <InputLabel id="demo-simple-select-outlined-label">
                      Logo Group
                </InputLabel>
                    <Select
                      labelId="demo-simple-select-outlined-label"
                      id="demo-simple-select-outlined"
                      value={group}
                      onChange={handleSelectChange}
                      label="Logo Group"
                      disabled={disabled}
                    >
                      <MenuItem value="">
                        <em>Logo Group</em>
                      </MenuItem>
                      {groupList && groupList.map(function (v, k) {
                        return (
                          <MenuItem value={v.id}>{v.nameEn}</MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>

              </Grid>
            </Box>
            <Box className={classes.sectionBox}>
              <Grid container item xs={12}>
                <Grid item xs={12}>
                  {defaultValue && (
                    <UploadImage
                      name="logoUrl"
                      data={defaultValue}
                      control={control}
                      Controller={Controller}
                      setValue={setValue}
                      getValues={getValues}
                      disabled={disabled}
                      uploadLabel="Upload image"
                      changeLabel="Change image"
                      uploadFolder="USINGOURLOGOS"
                    />
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>

          <div className={classes.buttonGroup}>
            {disabled || (
              <div className={classes.buttonGroup}>
                {/* <Button type="button" onClick={() => {}}>
                  Preview
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  onClick={() => {}}
                >
                  Save Draft
                </Button> */}
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  style={{
                    color: "white",
                    background: "#306F9D",
                    marginRight: "5px",
                    padding: "9px 16px",
                  }}
                >
                  Save Changes
                </Button>
                {match.params.id && match.params.id !== "new" && (
                  <Button
                    variant="contained"
                    disableElevation
                    style={{
                      color: "white",
                      background: "#962013",
                      padding: "9px 16px",
                    }}
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                )}
              </div>
            )}
          </div>
        </fieldset>
      </form>
    </Box>
  );
}

export default LogoEditPage;
